'use client';

import { use, useState, useMemo } from 'react';
import {
    getSchoolData,
    getSchoolName,
    getUnitStatus,
    getUnitChartData,
    SchoolUnit
} from '@/lib/data';
import StatsCard from "@/components/yayasan/dashboard/StatsCard";
import SchoolCard from "@/components/yayasan/dashboard/SchoolCard";
import CompactFinancialChart from "@/components/yayasan/dashboard/CompactFinancialChart";
import {
    Users,
    GraduationCap,
    Wallet,
    BarChart3,
    ArrowLeft,
    FileText,
    Search,
    Filter,
    Plus,
    School as SchoolIcon,
    Building2,
    Calendar,
    CheckCircle2,
    Clock,
    AlertCircle,
    TrendingUp,
    TrendingDown,
    ArrowRight,
    Info,
    AlertTriangle,
    ChevronDown
} from 'lucide-react';
import Link from 'next/link';
import { notFound } from 'next/navigation';
import MiniChart from '@/components/yayasan/dashboard/MiniChart';

export default function SchoolUnitPage({ params }: { params: Promise<{ unit: string }> }) {
    const { unit } = use(params);
    const validUnits: SchoolUnit[] = ['paud', 'paud-pekanbaru', 'paud-kampar', 'paud-siak', 'sd', 'smp', 'smk'];

    if (!validUnits.includes(unit as SchoolUnit)) {
        notFound();
    }

    const schoolUnit = unit as SchoolUnit;
    const schoolData = getSchoolData(schoolUnit);
    const schoolName = getSchoolName(schoolUnit);
    const schoolBasePath = `/yayasan/${unit}`;

    const [activeTab, setActiveTab] = useState<string>('overview');
    const [selectedSchoolId, setSelectedSchoolId] = useState<string>('all');

    // Extracting data dynamically based on unit
    const stats = (schoolData as any)[`${unit}Stats`] || [];
    const accounts = (schoolData as any)[`${unit}Accounts`] || [];
    const bankAccounts = (schoolData as any)[`${unit}BankAccounts`] || [];
    const transactions = (schoolData as any)[`${unit}Transactions`] || [];
    const subSchools = (schoolData as any)[`${unit}Schools`] || [];
    const chartData = (schoolData as any)[`${unit}ChartData`] || [];

    // Filter data based on selected school
    const displayedData = useMemo(() => {
        if (selectedSchoolId === 'all' || !subSchools || subSchools.length === 0) {
            // Show aggregated data for all schools
            return {
                stats,
                transactions,
                chartData, // Aggregated chart data
                subSchools,
                title: "Pemasukan vs Pengeluaran (Gabungan)"
            };
        }

        // Filter data for specific school
        const selectedSchool = subSchools.find((s: any) => s.id === selectedSchoolId);

        if (!selectedSchool) {
            return { stats, transactions, chartData, subSchools, title: "Pemasukan vs Pengeluaran" };
        }

        // Fetch specific chart data for the sub-unit
        const unitSpecificChartData = getUnitChartData(selectedSchoolId as SchoolUnit);

        return {
            stats: (schoolData as any)[`${selectedSchoolId.replace(/-([a-z])/g, (g) => g[1].toUpperCase())}Stats`] || stats, // Try to find specific stats or use default
            transactions: transactions.filter((t: any) => t.school === selectedSchool.name || !t.school),
            chartData: unitSpecificChartData,
            subSchools: [selectedSchool],
            title: `Pemasukan vs Pengeluaran - ${selectedSchool.name}`
        };
    }, [selectedSchoolId, stats, transactions, chartData, subSchools, schoolData]);

    // Quick Insights Calculation (Top 3) - using displayed data
    const topInsights = useMemo(() => {
        const schoolsToAnalyze = displayedData.subSchools;
        if (!schoolsToAnalyze || schoolsToAnalyze.length === 0) return null;

        const parseVal = (s: string) => parseFloat(s.replace(/[^\d.-]/g, ''));

        const largestBalance = [...schoolsToAnalyze].sort((a, b) => parseVal(b.balance) - parseVal(a.balance))[0];
        const highestExpense = [...schoolsToAnalyze].sort((a, b) => parseVal(b.expense) - parseVal(a.expense))[0];
        const highestReceivable = [...schoolsToAnalyze].filter(s => s.receivable).sort((a, b) => parseVal(b.receivable) - parseVal(a.receivable))[0];

        return {
            balance: largestBalance,
            expense: highestExpense,
            receivable: highestReceivable
        };
    }, [displayedData]);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'schools', label: 'Daftar Sekolah', icon: SchoolIcon },
        { id: 'finance', label: 'Keuangan', icon: Wallet },
        { id: 'reports', label: 'Laporan', icon: FileText },
    ];

    // --- UX DESIGNER LOGIC: Calculate Summary Cards with Breakdowns ---
    const formatIDR = (val: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            maximumFractionDigits: 0
        }).format(val);
    };

    const parseUnitValue = (val: string) => {
        // Handle "Rp 25.000.000" or "25 Jt"
        if (val.includes('Jt')) return parseFloat(val.replace(' Jt', '')) * 1000000;
        return parseFloat(val.replace(/[^\d.-]/g, '')) || 0;
    };

    const summaryCards = useMemo(() => {
        const isFiltered = selectedSchoolId !== 'all';
        const schools = subSchools || [];

        // Helper to get breakdown for a specific key
        const getBreakdown = (key: 'income' | 'expense' | 'balance' | 'receivable') => {
            return schools.map((s: any) => ({
                name: s.name,
                value: formatIDR(parseUnitValue(s[key]))
            }));
        };

        const totalIncome = schools.reduce((acc: number, s: any) => acc + parseUnitValue(s.income), 0);
        const totalExpense = schools.reduce((acc: number, s: any) => acc + parseUnitValue(s.expense), 0);
        const totalBalance = schools.reduce((acc: number, s: any) => acc + parseUnitValue(s.balance), 0);
        const totalReceivable = schools.reduce((acc: number, s: any) => acc + parseUnitValue(s.receivable), 0);

        const currentSchool = schools.find((s: any) => s.id === selectedSchoolId);

        return [
            {
                label: "Pendapatan Unit",
                value: isFiltered ? formatIDR(parseUnitValue(currentSchool?.income || '0')) : formatIDR(totalIncome),
                icon: TrendingUp,
                color: 'success' as const,
                change: isFiltered ? undefined : "+12.5%",
                breakdown: isFiltered ? undefined : getBreakdown('income')
            },
            {
                label: "Pengeluaran Unit",
                value: isFiltered ? formatIDR(parseUnitValue(currentSchool?.expense || '0')) : formatIDR(totalExpense),
                icon: TrendingDown,
                color: 'error' as const,
                change: isFiltered ? undefined : "-2.1%",
                breakdown: isFiltered ? undefined : getBreakdown('expense')
            },
            {
                label: "Saldo Kas Unit",
                value: isFiltered ? formatIDR(parseUnitValue(currentSchool?.balance || '0')) : formatIDR(totalBalance),
                icon: Wallet,
                color: 'primary' as const,
                change: isFiltered ? undefined : "+8.2%",
                breakdown: isFiltered ? undefined : getBreakdown('balance')
            },
            {
                label: "Piutang Siswa",
                value: isFiltered ? formatIDR(parseUnitValue(currentSchool?.receivable || '0')) : formatIDR(totalReceivable),
                icon: GraduationCap,
                color: 'warning' as const,
                change: isFiltered ? undefined : "+5.4%",
                breakdown: isFiltered ? undefined : getBreakdown('receivable')
            }
        ];
    }, [subSchools, selectedSchoolId]);

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-24">
            {/* Header Section */}
            <section className="bg-surface p-6 md:p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden group">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary-soft/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none transition-opacity duration-700 opacity-50 group-hover:opacity-100"></div>

                <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="w-16 h-16 gradient-green rounded-2xl flex items-center justify-center text-white shadow-xl shadow-primary/20 shrink-0">
                                <SchoolIcon className="w-8 h-8" />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">{schoolName}</h1>
                                <div className="flex items-center gap-4 mt-2">
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-success-soft text-success rounded-full border border-success/10 text-[10px] font-bold uppercase tracking-wider">
                                        <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></div>
                                        {selectedSchoolId === 'all' ? 'Agregasi Unit' : 'Filter Aktif'}
                                    </div>
                                    <div className="flex items-center gap-1.5 text-secondary text-xs font-medium">
                                        <Building2 className="w-3.5 h-3.5" />
                                        {selectedSchoolId === 'all' ? 'Seluruh Sekolah dalam Unit' : subSchools.find((s: any) => s.id === selectedSchoolId)?.name}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <Link href="/yayasan" className="px-5 py-3 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-primary-soft/50 transition-all shadow-sm uppercase tracking-widest flex items-center gap-2">
                                <ArrowLeft className="w-4 h-4" /> Kembali
                            </Link>
                            <button className="px-5 py-3 gradient-green text-white rounded-2xl text-[10px] font-bold hover:shadow-lg hover:shadow-primary/25 transition-all uppercase tracking-widest flex items-center gap-2">
                                <FileText className="w-4 h-4" /> Download Laporan
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* School Selector - Primary Filter Mechanism */}
            <div className="bg-surface rounded-3xl border border-border-main p-6 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl ${selectedSchoolId !== 'all' ? 'bg-primary text-white' : 'bg-primary-soft text-primary'}`}>
                            <Filter className="w-5 h-5" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-foreground">Filter Tampilan Data</h3>
                            <p className="text-xs text-muted">Akses data spesifik sekolah atau lihat agregasi unit</p>
                        </div>
                    </div>
                    <div className="relative min-w-[320px]">
                        <select
                            className="w-full px-6 py-4 bg-background border border-border-main rounded-2xl text-sm font-bold text-foreground focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all outline-none appearance-none cursor-pointer"
                            value={selectedSchoolId}
                            onChange={(e) => setSelectedSchoolId(e.target.value)}
                        >
                            <option value="all">📊 Lihat Keseluruhan Data (Agregasi)</option>
                            <optgroup label="Daftar Sekolah Terdaftar">
                                {subSchools.map((school: any) => (
                                    <option key={school.id} value={school.id}>
                                        🏫 {school.name}
                                    </option>
                                ))}
                            </optgroup>
                        </select>
                        <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* CARD SUMMARY (Premium Design with Hover Breakdown) */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {summaryCards.map((card) => (
                    <StatsCard key={card.label} {...card} />
                ))}
            </section>

            {/* Navigation Tabs */}
            <div className="flex flex-wrap bg-surface p-1.5 rounded-3xl border border-border-main shadow-sm w-fit mx-auto md:mx-0">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2.5 px-6 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all ${activeTab === tab.id
                            ? 'bg-primary text-white shadow-lg shadow-primary/20'
                            : 'text-muted hover:text-primary hover:bg-primary-soft/30'
                            }`}
                    >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Tab Contents */}
            <section className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                {activeTab === 'overview' && (
                    <div className="space-y-8">

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Mini Chart (6 months) */}
                            <div className="lg:col-span-2">
                                <CompactFinancialChart
                                    data={displayedData.chartData}
                                    title={displayedData.title}
                                />
                            </div>

                            {/* Top 3 Quick Insights */}
                            <div className="space-y-6">
                                <div className="bg-surface rounded-4xl border border-border-main p-6 h-fit flex flex-col">
                                    <h3 className="font-bold text-sm mb-6 flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-primary" />
                                        Quick Insight Unit
                                    </h3>
                                    <div className="space-y-5 grow">
                                        {topInsights ? (
                                            <>
                                                <div className="space-y-1.5 group cursor-default">
                                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Saldo Terbesar</p>
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-bold text-sm text-foreground">{topInsights.balance.name}</p>
                                                        <p className="font-bold text-sm text-primary">{topInsights.balance.balance}</p>
                                                    </div>
                                                </div>
                                                <div className="h-px bg-border-main/50"></div>
                                                <div className="space-y-1.5 group cursor-default">
                                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Pengeluaran Tertinggi</p>
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-bold text-sm text-foreground">{topInsights.expense.name}</p>
                                                        <p className="font-bold text-sm text-error">{topInsights.expense.expense}</p>
                                                    </div>
                                                </div>
                                                <div className="h-px bg-border-main/50"></div>
                                                <div className="space-y-1.5 group cursor-default">
                                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Piutang Terbanyak</p>
                                                    <div className="flex items-center justify-between">
                                                        <p className="font-bold text-sm text-foreground">{topInsights.receivable.name}</p>
                                                        <p className="font-bold text-sm text-warning">{topInsights.receivable.receivable}</p>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <p className="text-xs text-secondary italic">Data tidak tersedia</p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setActiveTab('schools')}
                                        className="mt-8 w-full py-3 bg-primary-soft text-primary rounded-xl text-[10px] font-bold uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-primary hover:text-white transition-all group"
                                    >
                                        Lihat Detail Unit <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Quick Charts for each unit */}



                        {/* Secondary Summary (Optional, but kept clean) */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2">
                                <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden h-fit">
                                    <div className="p-5 border-b border-border-main flex justify-between items-center bg-background/30">
                                        <div className="flex items-center gap-3">
                                            <Calendar className="w-4 h-4 text-primary" />
                                            <h4 className="font-bold text-sm">Aktivitas Prioritas</h4>
                                        </div>
                                        <Link href="#" onClick={() => setActiveTab('finance')} className="text-[9px] font-bold text-primary uppercase tracking-widest hover:underline">Kelola Keuangan</Link>
                                    </div>
                                    <div className="divide-y divide-border-main">
                                        {displayedData.transactions.slice(0, 3).map((trx: any) => (
                                            <div key={trx.id} className="p-4 flex items-center justify-between hover:bg-primary-soft/10 transition-colors">
                                                <div className="flex items-center gap-4">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${trx.type === 'income' ? 'bg-success-soft text-success' : 'bg-error-soft text-error'}`}>
                                                        {trx.type === 'income' ? <Plus className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-[13px] text-foreground">{trx.description}</p>
                                                        <p className="text-[9px] text-muted font-medium uppercase tracking-wider">{trx.date}</p>
                                                    </div>
                                                </div>
                                                <p className={`font-bold text-xs ${trx.type === 'income' ? 'text-success' : 'text-error'}`}>
                                                    {trx.type === 'income' ? '+' : '-'}{trx.amount}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className={`bg-linear-to-br ${getUnitStatus(unit).color} rounded-4xl p-6 text-white shadow-lg flex flex-col justify-center text-center transition-all h-[]`}>
                                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                                    {getUnitStatus(unit).icon === 'check' && <CheckCircle2 className="w-6 h-6 text-white" />}
                                    {getUnitStatus(unit).icon === 'info' && <Info className="w-6 h-6 text-white" />}
                                    {getUnitStatus(unit).icon === 'alert' && <AlertTriangle className="w-6 h-6 text-white" />}
                                </div>
                                <h4 className="font-bold text-base mb-1">Status {getUnitStatus(unit).title}</h4>
                                <p className="text-white/70 text-[10px] leading-relaxed">{getUnitStatus(unit).desc}</p>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'schools' && (
                    <section className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-foreground">Unit Sekolah Terintegrasi</h3>
                            <button className="px-4 py-2 bg-primary-soft text-primary rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-primary hover:text-white transition-all">Tambah Sekolah Baru</button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {subSchools.map((school: any) => (
                                <SchoolCard
                                    key={school.id}
                                    {...school}
                                />
                            ))}
                        </div>
                    </section>
                )}

                {activeTab === 'finance' && (
                    <div className="space-y-8 animate-in fade-in duration-500">
                        {/* 1. Kartu Ringkasan Keuangan (Berbasis Periode) */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-surface p-7 rounded-4xl border border-border-main shadow-sm flex items-center gap-5 transition-all hover:shadow-md">
                                <div className="w-14 h-14 bg-success-soft text-success rounded-2xl flex items-center justify-center shadow-inner">
                                    <TrendingUp className="w-7 h-7" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Total Setoran Kas</p>
                                    <p className="text-2xl font-bold text-foreground">Rp 25.500.000</p>
                                    <p className="text-[9px] text-success font-bold mt-1">Periode: Feb 2025</p>
                                </div>
                            </div>
                            <div className="bg-surface p-7 rounded-4xl border border-border-main shadow-sm flex items-center gap-5 transition-all hover:shadow-md">
                                <div className="w-14 h-14 bg-primary-soft text-primary rounded-2xl flex items-center justify-center shadow-inner">
                                    <ArrowRight className="w-7 h-7" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Total Transfer Unit</p>
                                    <p className="text-2xl font-bold text-foreground">Rp 18.800.000</p>
                                    <p className="text-[9px] text-primary font-bold mt-1">Inter-unit Terkonsolidasi</p>
                                </div>
                            </div>
                            <div className="bg-surface p-7 rounded-4xl border border-border-main shadow-sm flex items-center gap-5 transition-all hover:shadow-md">
                                <div className="w-14 h-14 bg-info-soft text-info rounded-2xl flex items-center justify-center shadow-inner">
                                    <FileText className="w-7 h-7" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest mb-1">Jumlah Akun Aktif</p>
                                    <p className="text-2xl font-bold text-foreground">{accounts.length} Akun</p>
                                    <p className="text-[9px] text-info font-bold mt-1">Sesuai COA Standar</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                            <div className="lg:col-span-2 space-y-10">

                                {/* 4. Aktivitas Keuangan Terakhir (Read-Only) */}
                                <div className="space-y-10">
                                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col">
                                        <div className="p-6 border-b border-border-main bg-background/30 flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-success animate-pulse"></div>
                                                <h4 className="font-bold text-sm">Setoran Terakhir</h4>
                                            </div>
                                            <span className="text-[9px] font-bold text-muted uppercase tracking-widest px-2 py-0.5 border border-border-main rounded-md">Real-time</span>
                                        </div>
                                        <div className="p-3 space-y-1 grow">
                                            {(schoolData as any)[`${unit}Deposits`]?.slice(0, 5).map((dep: any) => (
                                                <div key={dep.id} className="p-4 hover:bg-background rounded-2xl transition-all group flex justify-between items-center border border-transparent hover:border-border-main">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-success-soft text-success rounded-xl flex items-center justify-center transition-transform group-hover:scale-110">
                                                            <Plus className="w-4 h-4" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">{dep.description}</p>
                                                            <p className="text-[10px] text-muted font-medium mt-0.5">{dep.date} • <span className="text-primary">{dep.status}</span></p>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm font-bold text-success tabular-nums">+{dep.amount}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <Link href={`${schoolBasePath}/keuangan/setoran`} className="m-4 mt-0 py-3 border border-border-main border-dashed hover:border-primary hover:text-primary rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all text-center">
                                            Lihat Semua Setoran
                                        </Link>
                                    </div>

                                    <div className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden flex flex-col">
                                        <div className="p-6 border-b border-border-main bg-background/30 flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                                                <h4 className="font-bold text-sm">Transfer Terakhir</h4>
                                            </div>
                                            <span className="text-[9px] font-bold text-muted uppercase tracking-widest px-2 py-0.5 border border-border-main rounded-md">Internal</span>
                                        </div>
                                        <div className="p-3 space-y-1 grow">
                                            {(schoolData as any)[`${unit}Transfers`]?.slice(0, 5).map((trf: any) => (
                                                <div key={trf.id} className="p-4 hover:bg-background rounded-2xl transition-all group flex justify-between items-center border border-transparent hover:border-border-main">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 bg-primary-soft text-primary rounded-xl flex items-center justify-center transition-transform group-hover:scale-110">
                                                            <ArrowRight className="w-4 h-4" />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-bold text-foreground group-hover:text-primary transition-colors">Ke: {trf.toUnit}</p>
                                                            <p className="text-[10px] text-muted font-medium mt-0.5">{trf.date} • <span className="truncate max-w-[120px] inline-block align-bottom">{trf.description}</span></p>
                                                        </div>
                                                    </div>
                                                    <p className="text-sm font-bold text-error tabular-nums">-{trf.amount}</p>
                                                </div>
                                            ))}
                                        </div>
                                        <Link href={`${schoolBasePath}/keuangan/transfer`} className="m-4 mt-0 py-3 border border-border-main border-dashed hover:border-primary hover:text-primary rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all text-center">
                                            Lihat Semua Transfer
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-10">
                                {/* 3. Ringkasan Rekening Bank (Monitoring Mode) */}
                                <div className="bg-surface rounded-4xl border border-border-main shadow-sm p-8 relative overflow-hidden">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-primary-soft/10 rounded-full blur-3xl pointer-events-none"></div>
                                    <div className="relative z-10">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="font-bold text-lg flex items-center gap-3">
                                                <Wallet className="w-5 h-5 text-primary" />
                                                Rekening Bank
                                            </h3>
                                            <div className="px-2 py-1 bg-primary-soft rounded text-[9px] font-bold text-primary uppercase tracking-tighter">Read-Only</div>
                                        </div>
                                        <div className="space-y-6">
                                            {bankAccounts.slice(0, 2).map((acc: any) => (
                                                <div key={acc.id} className="p-6 bg-background rounded-3xl border border-border-main hover:border-primary/30 transition-all group relative overflow-hidden">
                                                    {acc.status === 'active' && <div className="absolute top-4 right-4 w-1.5 h-1.5 rounded-full bg-success"></div>}
                                                    <div className="flex items-center gap-3 mb-5">
                                                        <div className="px-3 py-1 bg-surface border border-border-main rounded-lg text-primary font-bold text-[10px] uppercase tracking-wider">
                                                            {acc.bankName}
                                                        </div>
                                                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">{acc.type}</p>
                                                    </div>

                                                    <p className="font-bold text-lg text-foreground mb-1 tracking-[0.2em] font-mono">{acc.accountNumber}</p>
                                                    <p className="text-xs text-secondary font-medium mb-6">{acc.accountName}</p>

                                                    <div className="pt-5 border-t border-border-main/50 space-y-2">
                                                        <div className="flex justify-between items-center text-[9px] uppercase tracking-widest font-bold">
                                                            <span className="text-muted">Terhubung COA</span>
                                                            <span className="text-primary">{acc.linkedAccount}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center text-[9px] uppercase tracking-widest font-bold">
                                                            <span className="text-muted">Status Akun</span>
                                                            <span className="text-success">{acc.status}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <Link href={`${schoolBasePath}/keuangan/rekening`} className="w-full mt-10 py-4 bg-primary-soft text-primary rounded-2xl text-[10px] font-bold uppercase tracking-widest hover:bg-primary hover:text-white transition-all shadow-sm flex items-center justify-center gap-2 group">
                                            <Search className="w-4 h-4 group-hover:scale-110 transition-transform" /> Lihat Semua Rekening
                                        </Link>
                                    </div>
                                </div>

                                {/* Financial Support & Audit */}
                                <div className="bg-linear-to-br from-primary to-primary-dark rounded-[2.5rem] p-8 text-white shadow-xl shadow-primary/20 relative overflow-hidden group">
                                    <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-1000"></div>
                                    <div className="relative z-10">
                                        <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-md">
                                            <AlertCircle className="w-6 h-6" />
                                        </div>
                                        <h4 className="font-bold text-xl mb-3">Audit Keuangan?</h4>
                                        <p className="text-white/80 text-xs leading-relaxed mb-8">Data ini tersinkronisasi langsung dengan buku besar pusat. Jika ada keraguan saldo, hubungi tim audit yayasan.</p>
                                        <button className="w-full py-4 bg-white text-primary hover:bg-primary-soft rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all shadow-lg">Konsultasi Auditor</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'reports' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { name: 'Jurnal Umum', desc: 'Catatan kronologis semua transaksi unit', icon: FileText, color: 'text-primary' },
                            { name: 'Buku Besar', desc: 'Ringkasan mutasi per akun', icon: BarChart3, color: 'text-info' },
                            { name: 'Laba Rugi', desc: 'Perhitungan surplus/defisit operasional', icon: Wallet, color: 'text-success' },
                            { name: 'Perubahan Modal', icon: Calendar, color: 'text-warning', desc: 'Riwayat aset bersih unit' },
                            { name: 'Neraca', icon: SchoolIcon, color: 'text-primary-dark', desc: 'Posisi aset dan liabilitas unit' }
                        ].map((report) => (
                            <div key={report.name} className="bg-surface p-6 rounded-4xl border border-border-main shadow-sm hover:border-primary hover:shadow-xl hover:shadow-primary/5 transition-all cursor-pointer group">
                                <div className={`w-12 h-12 rounded-2xl bg-background flex items-center justify-center ${report.color} mb-6 group-hover:scale-110 transition-transform`}>
                                    <report.icon className="w-6 h-6" />
                                </div>
                                <h4 className="font-bold text-lg text-foreground mb-2 group-hover:text-primary transition-colors">{report.name}</h4>
                                <p className="text-xs text-secondary leading-relaxed">{report.desc}</p>
                                <div className="mt-6 pt-6 border-t border-border-main flex items-center justify-between">
                                    <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Tersedia: PDF, Excel</span>
                                    <button className="text-[10px] font-bold text-primary uppercase tracking-widest hover:underline">Buka</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
}
